import { motion } from 'framer-motion';
import {
  Package,
  Palette,
  Users,
  MoreHorizontal,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import Card from '../models/card';
import Link from 'next/link';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import { Button } from '@nextui-org/react';
import routes from '@/config/routes';
import {
  characterJson,
  otherJson,
  productJson,
  stylesJson,
} from './featured-generators';
import AnchorLink from '../ui/links/anchor-link';
import { NSFW_TAGS } from '@/lib/constants/nsfw-tags';
import { cn } from '@/lib/utils';

// Updated sample data with titles
const tabContent = {
  character: characterJson,
  styles: stylesJson,
  product: productJson,
  more: otherJson,
};

export default function TypesSection({
  label = 'Featured Models',
  compact = false,
}: {
  label?: string;
  compact?: boolean;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [itemsPerRow, setItemsPerRow] = useState(8);
  const [itemsIndex, setItemsIndex] = useState(0);

  const breakpoint = useBreakpoint();
  const isSafeVersion = process.env.NEXT_PUBLIC_IS_SAFE === 'true';

  const tabs = useMemo(
    () => [
      { icon: Users, label: 'Character', content: tabContent.character },
      { icon: Palette, label: 'Styles', content: tabContent.styles },
      { icon: Package, label: 'Product', content: tabContent.product },
      { icon: MoreHorizontal, label: 'More', content: tabContent.more },
    ],
    []
  );

  // Update items per row based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerRow(2);
      } else if (window.innerWidth < 1024) {
        setItemsPerRow(2);
      } else {
        setItemsPerRow(4);
      }
      setItemsIndex(0);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create the content array with the train model card only once per render
  const currentContent = useMemo(() => {
    const content = [...tabs[activeTab].content];
    const trainModelCard = {
      slug: 'train-your-own-model',
      name: 'Train Your Own Model',
      isSpecial: true,
      image: { original: '', thumbnail: '' },
      ai_model: { name: '', slug: '' },
      ai_model_version_slug: '',
      id: 0,
      views_count: 0,
      generations_count: 0,
      description: '',
      type: { name: '', slug: '' },
      type_id: 0,
      shop: { name: '', slug: '' },
      shop_id: 0,
      orders_count: 0,
      total_downloads: 0,
      tags: [],
      preview_url: '',
      price: 0,
      sale_price: 0,
      is_external: false,
      external_product_url: '',
      external_product_button_text: '',
      has_tips: false,
      categories: [],
      tested: false,
      word_count: 0,
      ratings: 0,
      total_reviews: 0,
      store_review_score: 0,
      store_review_amount: 0,
      created_at: '',
      updated_at: '',
      gallery: [],
      type_slug: '',
      tag_slugs: [],
    };
    content.push(trainModelCard);

    // Filter out NSFW content if in safe version
    if (isSafeVersion) {
      const allNSFWTags = [...NSFW_TAGS.mature, ...NSFW_TAGS.explicit];
      return content.filter((card) => {
        // Keep special cards like train model card
        if (card.isSpecial) return true;
        // Filter out cards that have any of the NSFW tags
        return !card.tag_slugs?.some((tag: string) =>
          allNSFWTags.includes(tag)
        );
      });
    }

    return content;
  }, [activeTab, tabs, isSafeVersion]);

  // Calculate totalSlides based on the content length
  const totalSlides = useMemo(
    () => Math.ceil(currentContent.length / itemsPerRow),
    [currentContent.length, itemsPerRow]
  );

  const nextSlide = useCallback(
    () => setItemsIndex((prev) => (prev + 1) % totalSlides),
    [totalSlides]
  );

  const prevSlide = useCallback(
    () => setItemsIndex((prev) => (prev - 1 + totalSlides) % totalSlides),
    [totalSlides]
  );

  // Reset index when tab changes
  useEffect(() => {
    setItemsIndex(0);
  }, [activeTab]);

  return (
    <div className="relative w-full">
      <div className="relative w-full">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative flex w-full items-center md:w-auto">
            <div className="flex w-full max-w-[300px] gap-1 rounded-full bg-default-400/10 p-0.5 backdrop-blur-lg dark:bg-default-400/20 sm:max-w-none">
              {tabs.map((tab, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={cn(
                    'relative z-10 flex flex-1 items-center justify-center gap-1 rounded-full py-1.5 text-[10px] font-medium transition-colors sm:py-1.5 sm:text-xs',
                    'min-w-[60px] sm:min-w-[80px]',
                    activeTab === index
                      ? 'text-white'
                      : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                  )}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{tab.label}</span>
                </motion.button>
              ))}
              <motion.div
                className="absolute left-0.5 top-0.5 h-[26px] rounded-full bg-brand"
                style={{
                  width: `${100 / tabs.length}%`,
                }}
                initial={false}
                animate={{
                  x: `${activeTab * 100}%`,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            </div>
          </div>
        </div>

        <div className="relative flex items-center py-2">
          {itemsIndex > 0 && (
            <Button
              variant="light"
              isIconOnly
              className={`absolute left-0 top-1/2 z-10 h-10 w-10 min-w-0 -translate-y-1/2 rounded-full bg-white shadow-lg hover:bg-white/90 dark:bg-white dark:hover:bg-white/90 sm:h-12 sm:w-12 ${
                compact ? 'ml-2' : 'ml-4'
              }`}
              onClick={prevSlide}
            >
              <ChevronLeft className="h-5 w-5 text-gray-900" />
            </Button>
          )}

          <div className="flex-1 overflow-hidden">
            <motion.div
              className="flex w-full"
              animate={{ x: `-${itemsIndex * 100}%` }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.4 }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => {
                const sliceStart = slideIndex * itemsPerRow;
                const sliceEnd = (slideIndex + 1) * itemsPerRow;
                const sliceItems = currentContent.slice(sliceStart, sliceEnd);

                return (
                  <div key={slideIndex} className="w-full flex-shrink-0 px-0">
                    <div className="grid w-full grid-cols-2 gap-4 py-2 px-1 md:grid-cols-2 lg:grid-cols-4">
                      {sliceItems.map((item) => {
                        if (item.isSpecial) {
                          return (
                            <AnchorLink
                              key={item.slug}
                              href={routes.createGenerator}
                              className="h-full"
                            >
                              <motion.div
                                className="group relative flex h-full flex-col overflow-hidden rounded-lg bg-gradient-to-br from-brand to-brand-dark shadow-md transition-all"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                whileHover={{
                                  scale: 1.02,
                                  boxShadow:
                                    '0 10px 25px -5px rgb(var(--color-brand-rgb) / 0.3), 0 8px 10px -6px rgb(var(--color-brand-rgb) / 0.2)',
                                }}
                                transition={{
                                  duration: 0.3,
                                  ease: 'easeOut',
                                }}
                              >
                                <div className="flex aspect-square flex-grow items-center justify-center p-4">
                                  <div className="flex flex-col items-center gap-2">
                                    <div className="rounded-full bg-white/20 p-3">
                                      <Package className="h-6 w-6 text-white" />
                                    </div>
                                    <h3 className="text-base text-center font-medium text-white md:text-lg">
                                      {item.name}
                                    </h3>
                                  </div>
                                </div>

                                <div className="mt-3 flex h-[72px] flex-col justify-end px-4 pb-4">
                                  <Button
                                    className="group relative w-full overflow-hidden rounded-md bg-white/20 duration-300
                                      transition-all hover:bg-white/30"
                                    size="sm"
                                  >
                                    <div className="relative z-10 flex items-center justify-between px-2 py-1.5">
                                      <span className="text-xs font-medium text-white">
                                        Get Started
                                      </span>
                                      <ArrowRight className="h-3 w-3 text-white duration-300 transition-transform group-hover:translate-x-0.5" />
                                    </div>
                                  </Button>
                                </div>
                              </motion.div>
                            </AnchorLink>
                          );
                        }

                        return <Card key={item.slug} product={item} />;
                      })}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>

          {itemsIndex < totalSlides - 1 && (
            <Button
              variant="light"
              isIconOnly
              className={`absolute right-0 top-1/2 z-10 h-10 w-10 min-w-0 -translate-y-1/2 rounded-full bg-white shadow-lg hover:bg-white/90 dark:bg-white dark:hover:bg-white/90 sm:h-12 sm:w-12 ${
                compact ? 'mr-2' : 'mr-4'
              }`}
              onClick={nextSlide}
            >
              <ChevronRight className="h-5 w-5 text-gray-900" />
            </Button>
          )}
        </div>

        {totalSlides > 1 && (
          <div className="mt-4 flex justify-center gap-1.5">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                className={`h-1.5 rounded-full transition-all ${
                  index === itemsIndex
                    ? 'w-6 bg-brand'
                    : 'w-1.5 bg-gray-300 hover:bg-gray-400 dark:bg-dark-600 dark:hover:bg-dark-500'
                }`}
                onClick={() => setItemsIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
